/* Phiếu khai báo ban đầu của khách: cờ y khoa nổi lên đầu, các mục khách đã tích, chữ ký — dùng ở Phiếu tư vấn, Bác sĩ khám, Chuyển thực hiện */
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Tag } from 'antd';
import useAuthStore from '../../store/authStore';
import { ClipboardList } from 'lucide-react';
import { consultantOf } from '../Telesales/dashboard/data';
import { dmhm, doctorName, type Visit } from './logic';
import { useReceptionStaffs } from './declarationApi';
import { Card, Hint, Row, useScreening } from './shared';
import { useTuVan } from './store';

const list = (xs: string[], other?: string) => [...xs.filter((x) => x !== 'Khác'), ...(other ? [other] : [])].join(', ');

/** readOnly: chỉ đọc phiếu, không ghi tiến trình lượt (dùng ở màn ngoài App Tư vấn, không có lượt thật trên máy) */
export default function ScreeningView({ v, compact = false, readOnly = false, title = 'Khai báo ban đầu của khách' }: { v: Visit; compact?: boolean; readOnly?: boolean; title?: string }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const update = useTuVan((st) => st.update);
  const { s, flags, loading, error, forbidden, declId, edited, source } = useScreening(v);
  const canEdit = useAuthStore((st) => st.hasPermission('declaration.edit'));
  // Khách khai ở kiosk lúc máy này chưa có lượt: bổ sung vào tiến trình, đúng giờ khai
  useEffect(() => {
    if (readOnly || !declId || !s?.at) return;
    if (v.log.some((l) => l.text.includes('khai báo'))) return;
    update(v.id, () => ({}), { at: s.at, text: source === 'staff' ? 'Nhân viên nhập khai báo giúp khách' : 'Khách khai báo thông tin ban đầu', by: source === 'staff' ? 'Nhân viên' : s.writerName || s.name || 'Khách' });
  }, [declId, v.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const doctors = useReceptionStaffs('doctor', !compact);
  const consultants = useReceptionStaffs('consultant', !compact);
  const open = () => navigate(`/khai-bao?v=${v.id}`, { state: { back: `${loc.pathname}${loc.search}` } });
  if (loading) return <Card title={title}><Hint>Đang tải phiếu khai báo…</Hint></Card>;
  // Không đọc được phiếu (Lễ tân): chưa khai thì vẫn nhập giúp khách được, đã khai thì không mở form sửa
  if (forbidden) {
    return (
      <Card title={title}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Hint>{v.hasDecl ? `${error}. Khách đã khai${v.hasAlert ? ', phiếu có lưu ý y tế, báo bác sĩ xem phiếu' : ''}.` : `${error}. Khách chưa khai báo, đưa iPad cho khách điền.`}</Hint>
          {!compact && !v.hasDecl && <Button size="small" icon={<ClipboardList size={14} />} onClick={open}>Mở form khai báo</Button>}
        </div>
      </Card>
    );
  }
  // Tải lỗi mà báo "chưa khai" thì bác sĩ có thể bỏ sót cờ y khoa
  if (error && !s) return <Card title={title}><Hint>{error}. Tải lại trang để thử lại, chưa thể kết luận khách đã khai hay chưa.</Hint></Card>;
  if (!s) {
    return (
      <Card title={title}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Hint>Khách chưa khai báo. Kiosk tự mở form sau khi check-in; khách check-in ở quầy thì đưa iPad cho khách điền.</Hint>
          {!compact && <Button size="small" icon={<ClipboardList size={14} />} onClick={open}>Mở form khai báo</Button>}
        </div>
      </Card>
    );
  }
  return (
    <Card title={title} extra={<span className="text-xs text-gray-500">Khai {dmhm(s.at)}{edited ? ' · nhân viên đã sửa' : ''}</span>}>
      {flags.length > 0
        ? <div className="mb-3 flex flex-wrap gap-1.5">{flags.map((x) => <Tag key={x} color="red" className="!m-0">{x}</Tag>)}</div>
        : <div className="mb-3"><Tag color="green" className="!m-0">Không khai bệnh sử, thuốc đặc biệt</Tag></div>}
      <div className="space-y-1.5">
        {!compact && <Row label="Khách">{s.name}{s.gender === 1 ? ' · Nam' : s.gender === 2 ? ' · Nữ' : ''}{s.age ? ` · ${s.age} tuổi` : ''}{s.dob ? ` · ${s.dob}` : ''}</Row>}
        {s.blood && <Row label="Nhóm máu">{s.blood === 'Loại khác' ? s.bloodOther || 'Khác' : s.blood}</Row>}
        {(s.history.length > 0 || s.historyOther) && <Row label="Bệnh sử">{list(s.history, s.historyOther)}</Row>}
        {s.treatment && <Row label="Đã điều trị">{s.treatment}</Row>}
        {s.surgery && <Row label="Đã phẫu thuật">{s.surgeryNote || 'Có'}</Row>}
        {s.supplements.length > 0 && <Row label="TP chức năng">{s.supplements.join(', ')}</Row>}
        {(s.meds.length > 0 || s.medsOther) && <Row label="Thuốc đang dùng">{list(s.meds, s.medsOther)}</Row>}
        <Row label="Dễ bầm tím · hút thuốc">{s.bruise == null ? '—' : s.bruise ? 'Có' : 'Không'} · {s.smoke == null ? '—' : s.smoke ? 'Có' : 'Không'}</Row>
        {(s.interests.length > 0 || s.interestOther) && <Row label="Muốn tư vấn">{list(s.interests, s.interestOther)}</Row>}
        {!compact && s.channels.length > 0 && <Row label="Biết qua">{s.channels.join(', ')}{s.referrer ? ` · ${s.referrer}` : ''}</Row>}
        {!compact && (s.doctor || s.consultant) && (
          <Row label="Khách chỉ định">{[s.doctor && (doctors?.find((x) => String(x.id) === String(s.doctor))?.name ?? doctorName(s.doctor)), s.consultant && (consultants?.find((x) => String(x.id) === String(s.consultant))?.name ?? consultantOf(s.consultant)?.name)].filter(Boolean).join(' · ')}</Row>
        )}
        {!compact && s.job && <Row label="Nghề nghiệp">{s.job}{s.married != null ? ` · ${s.married ? 'Đã kết hôn' : 'Chưa kết hôn'}` : ''}</Row>}
      </div>
      {!compact && (
        <div className="mt-3 flex flex-wrap items-end justify-between gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
          <div className="text-xs text-gray-500">
            {s.writerRelation ? `Người bảo hộ (${s.writerRelation}): ${s.writerName ?? ''}` : 'Khách tự khai'}
            {s.signature && <img src={s.signature} alt="Chữ ký khách" className="mt-1 h-12 rounded border border-gray-200 bg-white" />}
          </div>
          {canEdit && <Button size="small" onClick={open}>Xem / sửa khai báo</Button>}
        </div>
      )}
    </Card>
  );
}
